import { LoggingService } from '@adaptiva/core/logging';
import { IMenuComponent, IMenuItem, MenuController, MenuItemDirectiveModule, MenuTriggerDirectiveModule } from '@adaptiva/core/menus';
import { AdaColor, BaseThemeService, IThemeModel } from '@adaptiva/core/themes';
import { BaseWidgetComponent, provideBaseWidget } from '@adaptiva/core/widgets';
import { IconComponent } from '@adaptiva/widgets/icon';
import { MenuComponentModule } from '@adaptiva/widgets/menu';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, inject, Injector, input, InputSignal, model, ModelSignal, NgModule, runInInjectionContext, Signal, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const DIRECTIVES: Type<NgModule>[] = [
	MenuItemDirectiveModule,
	MenuTriggerDirectiveModule,
];

const WIDGETS: Type<NgModule>[] = [
	IconComponent,
	MenuComponentModule,
];

@Component({
	selector: 'ada-inline-menu',
	templateUrl: './inline-menu.component.html',
	styleUrl: './inline-menu.component.scss',
	imports: [
		CommonModule,
		DIRECTIVES,
		WIDGETS
	],
	providers: [provideBaseWidget(() => InlineMenuComponent)],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class InlineMenuComponent<T = any> extends BaseWidgetComponent implements IMenuComponent<T> {

	public readonly controller: InputSignal<MenuController<T>> = input();

	public readonly data: ModelSignal<T> = model();

	public readonly isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public readonly items: ModelSignal<IMenuItem<T>[]> = model();

	protected readonly themes: BaseThemeService = inject(BaseThemeService);

	public readonly textColor: InputSignal<string> = input();

	protected readonly color: Signal<string> = computed(() => {
		const _textColor: string = this.textColor();
		const _theme: IThemeModel = this.themes.theme();

		if (_textColor)
			return _textColor;

		return AdaColor.fromRgb(_theme.foregroundColor).toRgbaString(75);
	});

	protected readonly injector: Injector = inject(Injector);

	protected readonly logger: LoggingService = inject(LoggingService);

	protected readonly menuControllerFx: EffectRef = effect(() => {
		const _controller: MenuController<T> = this.controller();

		if (!_controller)
			return;

		_controller.generate(this.data()).pipe(this.takeOneUntilDestroyed()).subscribe({
			next: x => this.items.set(x),
			error: (_err: Error): void => {
				this.logger.defaultErrorHandler('InlineMenuComponent.menuControllerFx', _err);
				this.items.set([]);
			}
		});
	});

	public select(e: MouseEvent, _item: IMenuItem<T>): void {
		e.stopPropagation();
		e.preventDefault();

		if (_item?.onSelect)
			runInInjectionContext(this.injector, () => _item.onSelect(_item));

		this.controller()?.dispatch(_item.name, _item);
	}

}
